/** A box in the drawing element's own frame, as percentages ready for CSS `left`/`top`/`width`/`height`. */
export interface ProjectedBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface ImageBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Where a box in image coordinates lands on the element drawing that image.
 *
 * Neither `object-fit` value maps image coordinates onto the element linearly, which is the whole
 * reason this exists: `cover` scales until the shorter axis fills and crops the overflow, so a box
 * drawn at its stored fractions sits visibly off the thing it bounds; `contain` scales until the
 * longer axis fits and letterboxes the rest, so the same box drifts the other way. A preview that
 * puts the rectangle somewhere other than the object is worse than no rectangle, because it reads
 * as the detection being wrong rather than the drawing.
 *
 * The result is deliberately not clamped. A box the crop cuts through really does run past the
 * element's edge, and saying so by overflowing (the container clips it) is honest; squeezing it to
 * fit would redraw it around a region nobody detected.
 */
export const projectBox = (
  box: ImageBox,
  /** The picture's own width divided by its height. */
  imageAspect: number,
  /** The drawing element's width divided by its height. */
  elementAspect: number,
  fit: 'cover' | 'contain',
): ProjectedBox => {
  // A ratio that cannot be reasoned about (a row with no recorded size, a zero-height element)
  // leaves the box where it was stored rather than scaling it by a nonsense factor.
  if (!(imageAspect > 0) || !(elementAspect > 0)) {
    return { left: box.x * 100, top: box.y * 100, width: box.w * 100, height: box.h * 100 };
  }

  const widerThanElement = imageAspect > elementAspect;

  if (fit === 'cover') {
    if (widerThanElement) {
      // Height fills exactly, so the vertical axis passes through untouched and only the
      // horizontal one is rescaled around the cropped-away margins.
      const visible = elementAspect / imageAspect;
      return {
        left: ((box.x - (1 - visible) / 2) / visible) * 100,
        top: box.y * 100,
        width: (box.w / visible) * 100,
        height: box.h * 100,
      };
    }
    const visible = imageAspect / elementAspect;
    return {
      left: box.x * 100,
      top: ((box.y - (1 - visible) / 2) / visible) * 100,
      width: box.w * 100,
      height: (box.h / visible) * 100,
    };
  }

  if (widerThanElement) {
    // Width fills exactly and the letterbox bars take the rest, so the box is scaled into the
    // drawn band and pushed down by half of what the bars occupy.
    const drawn = elementAspect / imageAspect;
    return {
      left: box.x * 100,
      top: ((1 - drawn) / 2 + box.y * drawn) * 100,
      width: box.w * 100,
      height: box.h * drawn * 100,
    };
  }
  const drawn = imageAspect / elementAspect;
  return {
    left: ((1 - drawn) / 2 + box.x * drawn) * 100,
    top: box.y * 100,
    width: box.w * drawn * 100,
    height: box.h * 100,
  };
};
